import React, { useCallback, useEffect, useState } from "react";
import apiFetch from "../../../context/api";

function fmtG(n) {
  return `${Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} g`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

/**
 * Informe de merma de dispensación (MERMA_PLAN.md v1).
 * Merma = peso real báscula − gramos cobrados, por línea de venta por peso.
 */
export default function MermaReport({ token, venueID }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState({ entries: [], total_merma_g: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!token || !venueID) return;
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const qs = params.toString();
      const res = await apiFetch(`/v1/venues/${venueID}/merma-report${qs ? `?${qs}` : ""}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Error al cargar el informe de merma");
      setData(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, venueID, from, to]);

  useEffect(() => { load(); }, [load]);

  const entries = data?.entries || [];

  return (
    <div className="space-y-4">
      {/* Filtros + total */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Desde</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="h-9 rounded-lg border border-gray-300 px-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Hasta</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className="h-9 rounded-lg border border-gray-300 px-2 text-sm" />
          </div>
          {(from || to) && (
            <button onClick={() => { setFrom(""); setTo(""); }}
              className="h-9 rounded-lg border border-gray-300 px-3 text-sm text-gray-600 hover:bg-gray-50">
              Limpiar
            </button>
          )}
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2">
          <div className="text-xs font-medium text-amber-700">Merma total</div>
          <div className="text-lg font-bold text-amber-800">{fmtG(data?.total_merma_g)}</div>
        </div>
      </div>

      {error && <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-2 text-sm">{error}</div>}

      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">Fecha</th>
              <th className="px-4 py-2.5 text-left font-semibold">Producto</th>
              <th className="px-4 py-2.5 text-left font-semibold">Socio</th>
              <th className="px-4 py-2.5 text-right font-semibold">Cobrado</th>
              <th className="px-4 py-2.5 text-right font-semibold">Real báscula</th>
              <th className="px-4 py-2.5 text-right font-semibold">Merma</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">Cargando…</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">Sin merma registrada en el período.</td></tr>
            ) : entries.map((e, i) => (
              <tr key={i} className="border-t border-gray-100">
                <td className="px-4 py-2.5 text-gray-700">{fmtDate(e.timestamp)}</td>
                <td className="px-4 py-2.5 text-gray-900 font-medium">{e.product_name || "—"}</td>
                <td className="px-4 py-2.5 text-gray-700">{e.socio || "—"}</td>
                <td className="px-4 py-2.5 text-right text-gray-700">{fmtG(e.grams_charged)}</td>
                <td className="px-4 py-2.5 text-right text-gray-700">{fmtG(e.grams_real)}</td>
                <td className={`px-4 py-2.5 text-right font-semibold ${Number(e.merma_g) > 0 ? "text-amber-700" : "text-gray-500"}`}>
                  {fmtG(e.merma_g)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
